// Patient accounts in Chat. The backend keeps the session in the HttpOnly
// cookie `chat_account`; the browser never sees a token. Calls keep fetch's
// default `credentials: 'same-origin'`, which sends the cookie wherever the
// frontend reaches the backend on its own origin (deployments, stacks, the
// Vite proxy). "include" would need CORS with credentials, and Chat's CORS
// admits every ivelion.com and nightingale-ai.com host.

// Optional chaining keeps the module importable from node:test, where
// import.meta.env is undefined.
const API_BASE_URL = import.meta.env?.VITE_API_URL || 'http://localhost:8000';

export interface AccountUser {
  email: string;
  firstName: string | null;
}

export type AccountResult<T> =
  | { ok: true; data: T }
  | { ok: false; status: number; code?: string; detail: string };

const FALLBACK_DETAIL: Record<number, string> = {
  400: 'Der Link ist ungültig oder abgelaufen.',
  401: 'E-Mail oder Passwort ist falsch.',
  429: 'Zu viele Versuche. Bitte warten Sie einen Moment und versuchen Sie es erneut.',
};
const GENERIC_DETAIL =
  'Das hat leider nicht geklappt. Bitte versuchen Sie es später erneut.';

interface ErrorBody {
  code?: unknown;
  detail?: unknown;
}

/**
 * Reads `code` and `detail` from an error answer. The backend may send them at
 * the top level or nested in FastAPI's `detail`; a FastAPI validation error
 * sends a list of `{msg}` objects.
 */
export function parseAccountError(
  status: number,
  body: unknown,
): { code?: string; detail: string } {
  let code: string | undefined;
  let detail: string | undefined;
  if (body && typeof body === 'object') {
    const top = body as ErrorBody;
    if (typeof top.code === 'string') code = top.code;
    if (typeof top.detail === 'string') {
      detail = top.detail;
    } else if (top.detail && typeof top.detail === 'object' && !Array.isArray(top.detail)) {
      const nested = top.detail as ErrorBody;
      if (typeof nested.code === 'string') code = nested.code;
      if (typeof nested.detail === 'string') detail = nested.detail;
    } else if (Array.isArray(top.detail)) {
      const messages = top.detail
        .map((item) =>
          item && typeof item === 'object' && typeof (item as { msg?: unknown }).msg === 'string'
            ? (item as { msg: string }).msg.replace(/^Value error, /, '')
            : null,
        )
        .filter((msg): msg is string => Boolean(msg));
      if (messages.length > 0) detail = messages.join(' ');
    }
  }
  return { code, detail: detail || FALLBACK_DETAIL[status] || GENERIC_DETAIL };
}

async function call<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<AccountResult<T>> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/account${path}`, {
      method,
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    return { ok: false, status: 0, detail: GENERIC_DETAIL };
  }

  let parsed: unknown = null;
  if (response.status !== 204) {
    try {
      parsed = await response.json();
    } catch {
      parsed = null;
    }
  }

  if (!response.ok) {
    return { ok: false, status: response.status, ...parseAccountError(response.status, parsed) };
  }
  return { ok: true, data: parsed as T };
}

interface MeResponse {
  authenticated: boolean;
  email?: string;
  first_name?: string | null;
}

interface LoginResponse extends MeResponse {
  claimed_chats?: number;
}

function toUser(me: MeResponse | null): AccountUser | null {
  if (!me?.authenticated || !me.email) return null;
  return { email: me.email, firstName: me.first_name ?? null };
}

export const accountAPI = {
  /** `{enabled: false}` also when the backend predates accounts. */
  async getConfig(): Promise<{ enabled: boolean }> {
    const result = await call<{ enabled?: boolean }>('GET', '/config');
    return { enabled: result.ok && result.data?.enabled === true };
  },

  async me(): Promise<AccountUser | null> {
    const result = await call<MeResponse>('GET', '/me');
    return result.ok ? toUser(result.data) : null;
  },

  async register(input: {
    email: string;
    password: string;
    firstName?: string;
    sessionId?: string | null;
  }): Promise<AccountResult<{ status: string }>> {
    return call('POST', '/register', {
      email: input.email,
      password: input.password,
      ...(input.firstName ? { first_name: input.firstName } : {}),
      accept_privacy: true,
      ...(input.sessionId ? { session_id: input.sessionId } : {}),
    });
  },

  async verify(token: string): Promise<AccountResult<{ status: string; email: string }>> {
    return call('POST', '/verify', { token });
  },

  async login(input: {
    email: string;
    password: string;
    sessionId?: string | null;
  }): Promise<AccountResult<{ user: AccountUser; claimedChats: number }>> {
    const result = await call<LoginResponse>('POST', '/login', {
      email: input.email,
      password: input.password,
      ...(input.sessionId ? { session_id: input.sessionId } : {}),
    });
    if (!result.ok) return result;
    const user = toUser(result.data) ?? { email: input.email, firstName: null };
    return { ok: true, data: { user, claimedChats: result.data?.claimed_chats ?? 0 } };
  },

  async logout(): Promise<AccountResult<null>> {
    return call('POST', '/logout');
  },

  async requestPasswordReset(email: string): Promise<AccountResult<null>> {
    return call('POST', '/password-reset', { email });
  },

  async confirmPasswordReset(token: string, password: string): Promise<AccountResult<null>> {
    return call('POST', '/password-reset/confirm', { token, password });
  },

  async deleteAccount(): Promise<AccountResult<null>> {
    return call('DELETE', '');
  },
};
